import { useSelector } from '@tanstack/react-store';
import type { ReactTable, RowData } from '@tanstack/react-table';
import { type Range, useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { debounce } from 'lodash-es';
import {
  type CSSProperties,
  type JSX,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DataTableContent } from './DataTableContent.js';
import { DataTableDragScrollLock } from './DataTableDragScrollLock.js';
import { DataTableEmptyState } from './DataTableEmptyState.js';
import { DataTableStatePlaceholder } from './DataTableStatePlaceholder.js';
import * as styles from './DataTableVirtualizationContainer.sty.js';
import { useFontsUpdated } from '../../../core/hooks/useFontsUpdated.js';
import { devLog } from '../../../core/utils/logging.js';
import { getDataTableBorderWidth } from '../../utils/data-table-border-width.js';
import {
  DATA_TABLE_DEFAULT_COLUMN_WIDTH,
  DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
  DATA_TABLE_VIRTUALIZER_COLUMN_OVERSCAN,
  DATA_TABLE_VIRTUALIZER_ROW_OVERSCAN,
} from '../constants.js';
import { addPinnedColumnsToVisibleRange } from '../features/ColumnPinning/column-pinning-utils.js';
import { isWidthExtendedConfig } from '../features/Layout/width-utils.js';
import type { TDataTableFeatures } from '../features/types/features.types.js';

type WidthConfigsById = Record<
  string,
  {
    minWidth: number;
    maxWidth: number | undefined;
    isContentOrAutoWidth: boolean;
  }
>;

export function DataTableVirtualizationContainer(
  props: Readonly<{
    table: ReactTable<TDataTableFeatures, RowData>;
    emptyState: ReturnType<typeof DataTableEmptyState>;
    fullHeight: boolean;
  }>,
): JSX.Element {
  const { table, emptyState, fullHeight } = props;

  const parentRef = useRef<HTMLDivElement | null>(null);

  const isLoading = table.atoms.loading?.get() ?? false;
  const [loadingScrollOffset, setLoadingScrollOffset] = useState(0);
  useLayoutEffect(() => {
    if (isLoading) {
      setLoadingScrollOffset(parentRef.current?.scrollTop ?? 0);
    }
  }, [isLoading]);

  const rows = table.getRowModel().rows;
  const visibleLeafColumns = table.getVisibleLeafColumns();

  // The ResizeObserver that sets the header height in DataTableHeader can fire mid-render,
  // and subscribing the whole DataTable to this atom caused a react warning.
  // Subscribing here isolates the re-render to this container, the only consumer
  // of header-height changes.
  useSelector(table.atoms.observedHeaderHeight);
  const totalHeaderHeight = table.getTotalHeaderHeight();

  const visibleLeafColumnsWithoutBuiltin =
    table.getVisibleLeafColumnsWithoutBuiltin();

  const getUniqueRowKey = useCallback(
    (index: number) => {
      if (index >= rows.length) {
        devLog(`[Table] Invalid row index ${index} (>= ${rows.length})`);
        // should not happen, but to avoid crashing an app, do this
        return -1;
      }
      return rows[index].id;
    },
    [rows],
  );

  const rowVirtualizer = useVirtualizer({
    count: rows?.length ?? 0,
    getScrollElement: () => parentRef.current,
    // Overscan of 5 was very ambitious. A single mouse scroll tick is usually
    // more than 5 condensed rows, so this is increased. In the future there could
    // be a possibility to make this dependent on the row density.
    overscan: DATA_TABLE_VIRTUALIZER_ROW_OVERSCAN,
    indexAttribute: 'data-row',
    // reserves space for the sticky header in the scrollable area, so the first rows are not hidden behind the header when scrolled to the top
    paddingStart: totalHeaderHeight,
    // ensures that scrollToIndex scrolls the item to the correct position, taking into account the reserved space for the header
    scrollPaddingStart: totalHeaderHeight,
    estimateSize: (index) => table.estimateRowHeight({ index }),
    measureElement: (element) => element.getBoundingClientRect().height,
    // Default wait until the scrollEnd is triggered and it starts updating.
    // https://tanstack.com/virtual/latest/docs/api/virtualizer#isscrollingresetdelay
    // Reducing it from the default 150ms seems to improve perceived scroll performance
    isScrollingResetDelay: 20,
    getItemKey: getUniqueRowKey,
    // reset scrollElement observers when the data in the table is changed
    // fixes the issue with Firefox browser not resetting the scrollTop position of the virtualizer when the table is scrolled and the data is changed
    // APPDEV-15467
    enabled: rows.length > 0,
  });

  const pinnedLeftColumns = table.getLeftVisibleLeafColumns();
  const pinnedRightColumns = table.getRightVisibleLeafColumns();

  // create left array indexes
  const pinnedLeftIndexes = useMemo(
    () => Array.from({ length: pinnedLeftColumns.length }, (_, i) => i),
    [pinnedLeftColumns.length],
  );

  // create right array indexes
  const pinnedRightIndexes = useMemo(() => {
    const pinnedRightStartIndex =
      visibleLeafColumns.length - pinnedRightColumns.length;

    return Array.from(
      { length: pinnedRightColumns.length },
      (_, i) => pinnedRightStartIndex + i,
    );
  }, [visibleLeafColumns.length, pinnedRightColumns.length]); // Only recreate when counts change

  const columnRangeExtractor = useCallback(
    (range: Range) =>
      addPinnedColumnsToVisibleRange(
        range,
        pinnedLeftIndexes,
        pinnedRightIndexes,
      ),
    [pinnedLeftIndexes, pinnedRightIndexes],
  );

  const orderedVisibleLeafColumns = table.getOrderedVisibleLeafColumns();
  const getUniqueColumnKey = useCallback(
    (index: number) => orderedVisibleLeafColumns[index].id,
    [orderedVisibleLeafColumns],
  );

  const widthConfigsById = useMemo<WidthConfigsById>(() => {
    return visibleLeafColumnsWithoutBuiltin.reduce<WidthConfigsById>(
      (acc, column) => {
        const { id, columnDef } = column;
        const {
          width,
          minWidth = DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
          maxWidth,
        } = columnDef;

        const isExtendedConfig = isWidthExtendedConfig(width);
        const isContentOrAutoWidth =
          isExtendedConfig || width === 'content' || width === 'auto';
        const maxAutoWidth = isExtendedConfig ? width.maxWidth : undefined;

        // Take the smallest of the maxWidths if both are defined
        // if columnSize is defined (the user resized a column), it takes precedence over maxWidth and maxAutoWidth
        const finalMaxWidth =
          maxWidth !== undefined && maxAutoWidth !== undefined
            ? Math.min(maxWidth, maxAutoWidth)
            : maxWidth ?? maxAutoWidth;

        acc[id] = {
          minWidth,
          maxWidth: finalMaxWidth,
          isContentOrAutoWidth,
        };
        return acc;
      },
      {},
    );
  }, [visibleLeafColumnsWithoutBuiltin]);

  const columnMeasure = useCallback(
    (element: Element) => {
      const measuredWidth = element.getBoundingClientRect().width;

      const columnId =
        element instanceof HTMLElement ? element.dataset.columnid : undefined;

      const previousWidth = columnId
        ? table.getMeasuredColumnWidth(columnId)
        : undefined;
      // Return the previous width if the element is hidden
      if (measuredWidth === 0 || !columnId) {
        return previousWidth ?? 0;
      }

      const widthConfiguration = widthConfigsById[columnId];

      // If no width configuration is found, the column is currently resized,
      // or an explicit column size is defined, return the measured width directly.
      // Column sizing already considers min/max widths correctly.
      if (
        table.atoms.columnSizing?.get()[columnId] !== undefined ||
        table.atoms.columnResizing?.get().isResizingColumn === columnId ||
        !widthConfiguration
      ) {
        table.setMeasuredColumnWidth(columnId, measuredWidth);
        return measuredWidth;
      }

      const { minWidth, maxWidth, isContentOrAutoWidth } = widthConfiguration;

      // For content/auto width columns we need to round up slightly,
      // to avoid an unnecessary ellipsis showing due to subpixel rendering issues.
      const roundedWidth = isContentOrAutoWidth
        ? Math.ceil(measuredWidth * 10) / 10
        : measuredWidth;

      const clampedWidth = Math.max(
        minWidth ?? 0,
        Math.min(maxWidth ?? Infinity, roundedWidth),
      );

      // Only update the measured width if it has changed beyond a small threshold (> 0.1px)
      // to prevent infinite loops (ever-growing columns) caused by subpixel rendering differences
      // at specific browser zoom and display scale combinations (e.g. 150% zoom + 125% scale).
      if (
        previousWidth !== undefined &&
        Math.abs(clampedWidth - previousWidth) <= 0.1
      ) {
        // Return the previous width to prevent the virtualizer from detecting
        // insignificant changes that would trigger re-renders.
        return previousWidth;
      }

      table.setMeasuredColumnWidth(columnId, clampedWidth);
      return clampedWidth;
    },
    [table, widthConfigsById],
  );

  const columnVirtualizer = useVirtualizer({
    count: visibleLeafColumns.length,
    getScrollElement: () => parentRef.current,
    indexAttribute: 'data-column',
    estimateSize: () => DATA_TABLE_DEFAULT_COLUMN_WIDTH,
    rangeExtractor: columnRangeExtractor,
    overscan: DATA_TABLE_VIRTUALIZER_COLUMN_OVERSCAN,
    horizontal: true,
    getItemKey: getUniqueColumnKey,
    measureElement: columnMeasure,
  });

  const hasVisibleColumns = visibleLeafColumnsWithoutBuiltin.length > 0;
  const estimatedRowHeight = table.estimateRowHeight();

  const visualVariantConfig = table.atoms.visualVariantConfig?.get();
  const containerStyle = useMemo(() => {
    let minHeight: CSSProperties['minHeight'] = undefined;

    if (totalHeaderHeight === 0) {
      // if headers are hidden, be at least one row tall, otherwise be at least two rows tall
      // to account for the border between header and first row we need to add 2px border
      minHeight =
        visualVariantConfig?.headers === 'hidden'
          ? estimatedRowHeight
          : estimatedRowHeight * 2 + getDataTableBorderWidth();
    } else {
      // or header size + 1 additional row
      minHeight = totalHeaderHeight + estimatedRowHeight;
    }

    if (visualVariantConfig?.contained) {
      // Add a borderTop and border bottom for the borders around the containment.
      minHeight += 2 * getDataTableBorderWidth();
    }
    const baseStyle: CSSProperties = {
      minHeight,
    };

    if (fullHeight) {
      baseStyle.height = '100%';
    }
    return baseStyle;
  }, [
    totalHeaderHeight,
    visualVariantConfig?.contained,
    visualVariantConfig?.headers,
    estimatedRowHeight,
    fullHeight,
  ]);

  const scrollRect = rowVirtualizer.scrollRect;
  const scrollElement = rowVirtualizer.scrollElement;
  const scrollWidth = scrollElement?.scrollWidth;
  const scrollHeight = scrollElement?.scrollHeight;

  const scrollDirection = useMemo(() => {
    if (
      scrollRect?.width &&
      scrollRect?.height &&
      scrollWidth !== undefined &&
      scrollHeight !== undefined
    ) {
      // Determine whether the scrollbars are visible to apply the according styles for the scrollbars
      const hasScrollbarX = scrollRect.width < scrollWidth;
      const hasScrollbarY = scrollRect.height < scrollHeight;

      if (hasScrollbarX && hasScrollbarY) {
        return 'xy';
      } else if (hasScrollbarX) {
        return 'x';
      } else if (hasScrollbarY) {
        return 'y';
      }
    }
    return false;
  }, [scrollRect?.width, scrollRect?.height, scrollWidth, scrollHeight]);

  const fontUpdateQueued = useRef(false);
  useFontsUpdated(() => {
    if (fontUpdateQueued.current) {
      return;
    }
    fontUpdateQueued.current = true;
    const controller = new AbortController();
    void Promise.race([
      // If the animations have not stopped after a timeout period,
      // we can remeasure nevertheless.
      new Promise<void>((resolve) =>
        setTimeout(() => {
          resolve();
        }, 1_000),
      ),
      // If the animations subside within a reasonable interval, we
      // can also remeasure.
      new Promise<void>((resolve) => {
        document.addEventListener(
          'animationend',
          debounce(() => {
            resolve();
          }, 200),
          { signal: controller.signal },
        );
      }),
    ])
      .then(() => {
        table.remeasureAllColumns();
      })
      .catch((error: unknown) => {
        devLog(
          '[DataTable] Column remeasurement after font update failed',
          error,
        );
      })
      .finally(() => {
        controller.abort();
        // Reset in finally so a remeasurement failure doesn't permanently block future font-update cycles.
        fontUpdateQueued.current = false;
      });
  });

  // If the column definition changes, make sure that the measurements set is cleared
  // of columns that no longer exist. This keeps the `measuredColumnWidths` cache in
  // sync with the virtualizer's `itemSizeCache`.
  const getAllLeafColumns = table.getAllLeafColumns;
  const getMeasuredColumnWidths = table.getMeasuredColumnWidths;
  const resetMeasuredColumnWidths = table.resetMeasuredColumnWidths;
  const tableColumns = table.options.columns;
  useEffect(() => {
    const currentColumnIds = new Set(getAllLeafColumns().map((col) => col.id));

    const removedIds: string[] = [];
    for (const id of getMeasuredColumnWidths().keys()) {
      // We compare the columns by their ids, since the virtualizer internally relies
      // on the ids (as defined by our getItemKey).
      if (!currentColumnIds.has(id)) {
        removedIds.push(id);
      }
    }
    if (removedIds.length > 0) {
      resetMeasuredColumnWidths(removedIds);
    }
  }, [
    getAllLeafColumns,
    getMeasuredColumnWidths,
    resetMeasuredColumnWidths,
    tableColumns,
  ]);

  table.registerVirtualizers(rowVirtualizer, columnVirtualizer);

  return (
    <div
      ref={parentRef}
      className={clsx(
        'strato-table-virtualization-container',
        styles.virtualizationContainer({
          contained: visualVariantConfig?.contained,
          rowSeparation: visualVariantConfig?.rowSeparation,
          headers: visualVariantConfig?.headers,
          loading: table.atoms.loading?.get(),
          hasVisibleColumns,
          scrollDirection,
          isDataEmpty: rows.length === 0,
        }),
      )}
      style={containerStyle}
    >
      <DataTableDragScrollLock table={table} parentRef={parentRef} />
      <DataTableContent
        rowVirtualizer={rowVirtualizer}
        columnVirtualizer={columnVirtualizer}
      />
      <DataTableStatePlaceholder
        isColumnsEmpty={columnVirtualizer.options.count === 0}
        isDataEmpty={rowVirtualizer.options.count === 0}
        allColumnsHidden={!hasVisibleColumns}
        emptyStateSlot={emptyState}
        scrollOffset={loadingScrollOffset}
      />
    </div>
  );
}
